import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/api/client";

export function PurgeTenantDialog({ tenant, onClose, onDone }: { tenant: { id: string; name: string }; onClose: () => void; onDone: () => void }) {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    const purge = async () => {
        setBusy(true);
        try {
            const r = await api.delete(`/platform/tenants/${tenant.id}/purge`, { timeout: 120000 });
            toast.success(t("platform.tenants.purge_success", { name: tenant.name, count: r.data?.deleted_documents ?? 0 }));
            onDone();
        } catch (e: any) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.5)" }} data-testid="tenant-purge-dialog" onClick={() => !busy && onClose()}>
            <div className="rounded-xl p-5 max-w-md w-[90%]" style={{ background: "var(--card)", color: "var(--text)", border: "1px solid var(--border)" }} onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle size={18} style={{ color: "var(--error-fg)" }} />
                    <h2 className="text-lg font-semibold">{t("platform.tenants.purge_title")}</h2>
                </div>
                <div className="text-sm mb-2">
                    <span style={{ color: "var(--text-muted)" }}>{t("platform.tenants.delete_target")} </span>
                    <strong data-testid="tenant-purge-name">{tenant.name}</strong>
                </div>
                <p className="text-sm mb-4 rounded-md p-3" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{t("platform.tenants.purge_body")}</p>
                <div className="flex justify-end gap-2">
                    <button data-testid="tenant-purge-cancel" disabled={busy} onClick={onClose} className="rounded-md border px-3 py-1.5 text-sm" style={{ borderColor: "var(--border)", color: "var(--text)" }}>
                        {t("platform.tenants.delete_no")}
                    </button>
                    <button data-testid="tenant-purge-confirm" disabled={busy} onClick={purge} className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium" style={{ background: "var(--error-fg)", color: "#fff" }}>
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        {t("platform.tenants.purge_confirm")}
                    </button>
                </div>
            </div>
        </div>
    );
}
